import { useEffect, useState } from 'react';

/**
 * /launch.json: the launch status, the token's contract address and its links (deploy/cloudflare/make-launch-json.mjs).
 * Fetched once per page load and shared. While status is "soon" there is no deployment or API yet, so the live views say
 * "launching soon" instead of reporting the missing feed as a disconnection.
 */
export interface LaunchInfo {
  status: 'soon' | 'live';
  chainName: string;
  x?: string;
  token: string | null;
  quote?: string;
  links: { label: string; url: string }[];
  /** Scheduled main-event races (Season 1). Absent = no schedule shown. Times are "HH:MM" UTC. */
  /** shoe: the on-chain Golden Shoe size (USDG); shoeBonus: added off-chain for the winner; anyAppPrize: off-chain draw
   *  among buyers on any app. */
  races?: {
    label?: string;
    times: string[];
    prize?: string;
    from?: string;
    display?: string;
    shoe?: number;
    shoeBonus?: number;
    anyAppPrize?: number;
    /** Hours before a race when buys start counting for the Any-App draw. */
    anyAppEarlyHours?: number;
  };
  /** A one-off event skin. Absent (or an unknown kind) = the normal site. kind "halloween" turns on RUN DAY: the theme,
   *  the event panel on the race page and jackpot copy in the race bar/rules. Times are "HH:MM" UTC on `date`. */
  event?: {
    kind: 'halloween' | (string & {});
    name?: string;
    date: string;
    times: string[];
    /** Scheduled length of each race. Default 13. */
    raceMinutes?: number;
    /** Forks per race and the most each Forge gate buys back and burns (USD). */
    forks?: number;
    forkBurnCap?: number;
  };
  /** Default look for viewers who haven't picked one (they can switch any time). Absent = the runner. */
  skin?: 'runner' | 'crab' | 'roadster';
}

let pending: Promise<LaunchInfo | null> | null = null;
const load = () =>
  (pending ??= fetch('/launch.json', { cache: 'no-store' })
    .then((r) => (r.ok ? (r.json() as Promise<LaunchInfo>) : null))
    .catch(() => null));

/** The shared /launch.json fetch, for non-React code (the canvas renderer). */
export const launchInfo = load;

export function useLaunchInfo(): LaunchInfo | null {
  const [info, setInfo] = useState<LaunchInfo | null>(null);
  useEffect(() => {
    let live = true;
    load().then((i) => live && setInfo(i));
    return () => {
      live = false;
    };
  }, []);
  return info;
}

/** True before the deployment exists (launch.json says "soon"). */
export function usePrelaunch(): boolean {
  return useLaunchInfo()?.status === 'soon';
}
